import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"
import { getCurrentUser } from "../api/authService"
import { AUTH_TOKEN_KEY } from "../utils/constants"

interface AuthUser {
  _id: string
  name: string
  email: string
  phone?: string
  // Different parts of the backend model role differently — some accounts
  // carry `usertype`/`usertypeId` (via the Usertype collection), others carry
  // a plain `role` string and/or `isAdmin` boolean directly on the User doc.
  // We check all of them so either model resolves to the right role.
  usertype?: string | null
  usertypeId?: string | null
  role?: string | null
  isAdmin?: boolean
  [key: string]: any
}

interface AuthContextValue {
  user: AuthUser | null
  role: string | null
  isAuthenticated: boolean
  hasRole: (allowedRoles: string[]) => boolean
  refreshUser: () => Promise<void>
  // Synchronously updates context state (and localStorage) right after
  // login, so role-gated routes/menus see the correct role immediately
  // instead of waiting for the next async refreshUser() poll to catch up.
  setUserData: (user: AuthUser) => void
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

// How often we re-check the current role with the backend. Kept short enough
// that a role change (e.g. made directly in the DB, or by an admin) reaches
// an already-logged-in user without requiring them to log out and back in.
const ROLE_REFRESH_INTERVAL_MS = 60_000

const readStoredUser = (): AuthUser | null => {
  try {
    const stored = localStorage.getItem("userData")
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(readStoredUser)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY)
    if (!token) return

    try {
      const response = await getCurrentUser()
      if (response.status === 200 && response.data) {
        const updated = { ...readStoredUser(), ...response.data }
        localStorage.setItem("userData", JSON.stringify(updated))
        setUser(updated)
      }
    } catch {
      // Network/server hiccup — keep whatever role we already have rather
      // than logging the user out or blocking the UI.
    }
  }, [])

  useEffect(() => {
    // Pick up role changes made while the tab was unauthenticated/just loaded.
    refreshUser()

    intervalRef.current = setInterval(refreshUser, ROLE_REFRESH_INTERVAL_MS)

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshUser()
      }
    }
    document.addEventListener("visibilitychange", onVisibilityChange)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      document.removeEventListener("visibilitychange", onVisibilityChange)
    }
  }, [refreshUser])

  const role = user?.usertype
    ? String(user.usertype).toLowerCase()
    : user?.role
    ? String(user.role).toLowerCase()
    : user?.isAdmin
    ? "admin"
    : null

  const hasRole = useCallback(
    (allowedRoles: string[]) => {
      if (!role) return false
      return allowedRoles.some((allowed) => allowed.toLowerCase() === role)
    },
    [role]
  )

  const setUserData = useCallback((newUser: AuthUser) => {
    localStorage.setItem("userData", JSON.stringify(newUser))
    setUser(newUser)
  }, [])

  const value: AuthContextValue = {
    user,
    role,
    isAuthenticated: !!localStorage.getItem(AUTH_TOKEN_KEY),
    hasRole,
    refreshUser,
    setUserData,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuthContext = (): AuthContextValue => {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error("useAuthContext must be used within an AuthProvider")
  }
  return ctx
}
